import { Injectable } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  Route,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { filter, startWith, Subscription, tap } from 'rxjs';
import { StoreRegistryService } from './store-registry.service';
import { HyperionContext } from '../models/base-entity';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  private initialized = false;
  private routerSubscription: Subscription = new Subscription();
  private selectedId: string | null | undefined;
  private sidebarIdFromRoute: string | null | undefined;
  private editFormOverlayHeader = '';
  private editFormOverlayIcon = '';
  private activeEntityType = '';
  private cancelLevel?: number = undefined;
  private helpFromRoute: string | null = null;

  context: HyperionContext | null = null;
  slug: string | null = null;
  store: any;
  storeFromRoute: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storeRegistry: StoreRegistryService
  ) {}

  /**
   * Get the first URL segment as a string
   * @returns {string | null} The first segment of the URL
   */
  public getRootSegment(): string | null {
    const urlWithoutParams = this.router.url.split('?')[0];
    const segments = urlWithoutParams
      .split('/')
      .filter(segment => segment.length > 0);
    return segments.length > 0 ? segments[0] : null;
  }

  getRootByContext(context?: HyperionContext, slug?: string): string {
    if (!context) {
      context = this.context || undefined;
    }
    if (!slug) {
      slug = this.getParameterFromRoute('slug') || undefined;
    }
    switch (context) {
      case 'atlasInternal':
        return `/atlas-intern/`;
      case 'atlasExternal':
        return `/atlas/${slug}/`;
      default:
        return `/data-backend/`;
    }
  }

  /**
   * initialize route-subscription
   */
  public initiateRouteSubscription() {
    if (!this.initialized) this.setupRouteSubscription();
    this.initialized = true;
  }

  /**
   * Initiates subscriptions for monitoring router events and triggers appropriate actions upon navigation events.
   * It sets up a subscription to listen for `NavigationEnd` events and executes the `lookupAtlasRoute` method.
   * It also ensures the initial application load is handled and marks the component as initialized.
   *
   * @return {void} This method does not return a value.
   */
  public initiateAtlasSubscriptions(): void {
    this.routerSubscriptionCheck();
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(this.router.url) // Ensure initial load is handled
      )
      .subscribe(() => {
        this.lookupAtlasRoute();
      });
    this.initialized = true;
  }

  /**
   * Determines whether the sidebar should be displayed based on the route and a given sidebar ID.
   *
   * @param {string} sidebarId - The ID of the sidebar to match against the current route.
   * @return {boolean} Returns true if the sidebar should be shown, otherwise false.
   */
  public showSidebarByRoute(sidebarId: string): boolean {
    const entityId = this.getIdFromRoute();
    return !!(entityId && this.sidebarIdFromRoute === sidebarId);
  }

  /**
   * Initializes and sets up the subscription to router events in order
   * to handle route changes. Ensures there is only one active subscription
   * at a time and triggers data lookup whenever navigation ends or during
   * the initial load.
   *
   * @return {void} No return value.
   */
  private setupRouteSubscription(): void {
    // Ensure there's only one subscription
    this.routerSubscriptionCheck();
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(this.router.url) // Ensure initial load is handled
      )
      .subscribe(() => {
        this.lookupDataFromRoute();
      });
  }

  /**
   * Retrieves and processes route-specific data for the component.
   * Subscribes to the data object of the deepest child route, extracts relevant information such as cancel level,
   * entity type, sidebar ID, overlay text, and help keywords, and applies this data to the component's respective state variables.
   * Executes additional methods for routing-based lookups and keyword settings.
   *
   * @return {void} This method does not return a value.
   */
  lookupDataFromRoute(): void {
    this.getDeepestChild(this.route)?.data.subscribe(data => {
      this.cancelLevel = data['cancelLevel'] || undefined;
      if (data['entityType'] && !data['excludeFromRouteSelection']) {
        this.sidebarIdFromRoute = data['sidebarId'];
        this.activeEntityType = data['entityType'];
        this.editFormOverlayHeader = `${data['overlayText']} ${this.selectedId === 'neu' ? 'hinzufügen' : 'bearbeiten'}`;
        this.selectedId = this.getIdFromRoute();
        this.editFormOverlayIcon =
          this.selectedId === 'neu' ? 'fas fa-plus-circle' : 'fas fa-pen';

        this.helpFromRoute = data['helpKeywords'] || null;

        this.lookupStoreFromRoute();
        this.setHelpKeywordsGlobal();
      }
    });
  }

  /**
   * Processes and handles routing information by managing story and topic parameters
   * and subscribing to route data to perform necessary updates or operations.
   *
   * @return {void} This method does not return a value.
   */
  lookupAtlasRoute(): void {
    const GLOBALS_STORE = 'Globals';

    // Handle active story
    this.handleStoryParameter();

    // Handle active topic
    this.handleTopicParameter();

    // Handle subscribed route data
    this.getDeepestChild(this.route)?.data.subscribe(data => {
      this.handleRouteData(data, GLOBALS_STORE);
    });
  }

  /**
   * Handles the "story" parameter obtained from the route query and sets it as the active story in the relevant store, if available.
   *
   * @return {void} Does not return any value.
   */
  private handleStoryParameter(): void {
    const storyFromParameter = this.getQueryParamFromRoute('story');

    if (storyFromParameter) {
      this.storeFromRoute = this.storeRegistry.getStore('Globals');
      this.storeFromRoute.setActiveStory(storyFromParameter);
    }
  }

  /**
   * Handles the "topic" query parameter from the route, retrieves the corresponding topic,
   * and triggers an event to load the specified topic in the application.
   *
   * Retrieves the "topic" parameter from the route, finds the associated topic
   * in the "Topic" store, and uses the store from the route to trigger an event
   * to load the topic into the application's global state after a delay.
   *
   * @return {void} This method does not return a value.
   */
  private handleTopicParameter(): void {
    const topicFromParameter = this.getQueryParamFromRoute('topic');
    if (topicFromParameter) {
      const topicStore = this.storeRegistry.getStore('Topic');
      setTimeout(() => {
        const topic = topicStore.getById(topicFromParameter);
        this.storeFromRoute = this.storeRegistry.getStore('Globals');
        this.storeFromRoute.triggerAtlasEvent('loadTopic', { topic });
      }, 1500); // TODO: Improve async data handling
    }
  }

  /**
   * Handles the routing data to determine the appropriate store and updates the application's state accordingly.
   *
   * @param {any} data The routing data object containing information such as entityType and context.
   * @param {string} globalsStoreKey The key to retrieve the global store from the store registry.
   * @return {void} No return value. The method updates internal properties and the active state of the store.
   */
  private handleRouteData(data: any, globalsStoreKey: string): void {
    this.selectedId = this.getIdFromRoute();
    const entityType = data['entityType'];
    const isNewEntry = this.selectedId === 'neu';

    switch (entityType) {
      case 'Report':
        this.storeFromRoute = this.storeRegistry.getStore(globalsStoreKey);
        // TODO: Not implemented yet
        break;

      case 'Story':
        this.storeFromRoute = this.storeRegistry.getStore(globalsStoreKey);
        this.storeFromRoute.setActiveStory(this.selectedId);
        break;

      default:
        this.storeFromRoute = this.storeRegistry.getStore('Grid');
        this.storeFromRoute.setActiveBookmarkById(this.selectedId);
    }

    this.editFormOverlayIcon = isNewEntry ? 'fas fa-plus-circle' : 'fas fa-pen';
    this.context = data['context'];
  }

  /**
   * Determines and sets the appropriate store based on the current route and active entity type.
   * If a valid store is identified, it ensures proper single selection of the entity.
   * If no valid store or entity is found, it unsets the relevant route data.
   *
   * @return {void} This method does not return a value.
   */
  lookupStoreFromRoute(): void {
    this.storeFromRoute = this.storeRegistry.getStore(this.activeEntityType);

    if (
      this.storeFromRoute &&
      (this.storeFromRoute.allIds().includes(this.selectedId) ||
        this.selectedId === 'neu')
    ) {
      this.storeFromRoute.singleSelection(this.selectedId);
    } else {
      this.unsetRouteData();
    }
  }

  /**
   * Sets global help keywords based on the current route.
   * This method retrieves the global store from the store registry
   * and sets its keywords using the `helpFromRoute` property.
   *
   * @return {void} Does not return a value.
   */
  setHelpKeywordsGlobal(): void {
    if (this.helpFromRoute) {
      const globalsStore = this.storeRegistry.getStore('Globals');
      globalsStore.setByKeywords(this.helpFromRoute);
    }
  }

  /**
   * Resets the route-related data by unsetting the selected ID, the store derived from the route,
   * and any other associated properties to their default value.
   *
   * @return {void} This method does not return a value.
   */
  unsetRouteData() {
    this.selectedId = undefined;
    this.storeFromRoute = undefined;
    //this.activeEntityType = '';
  }

  /**
   * Retrieves the sidebar ID based on the current route.
   *
   * @return {string} The sidebar ID derived from the route.
   */
  public getSidebarIdFromRoute(): string | null | undefined {
    return this.sidebarIdFromRoute;
  }

  /**
   * Retrieves the currently selected ID from the route.
   *
   * @return {string | number} The selected ID associated with the route.
   */
  public getSelectedIdFromRoute(): string | null | undefined {
    return this.selectedId;
  }

  /**
   * Navigates back to the root level of the current route hierarchy
   * by replacing the current route segment with the parent route.
   *
   * @return {void} Does not return a value.
   */
  public navigateBackHard(): void {
    this.router.navigate(['./'], {
      relativeTo: this.route,
    });
  }

  /**
   * back to the roots
   */
  public navigateRoot(): void {
    this.router.navigate(['/data-backend']);
  }

  /**
   * Opens a bookmark route by its ID. Depending on the context, it either navigates
   * to the route directly or opens it in a new browser tab.
   *
   * @param {string} bookmarkId - The unique identifier of the bookmark to open.
   * @param {boolean} [newTab=false] - Specifies whether to open the route in a new browser tab. Defaults to false.
   * @return {Promise<boolean>} A promise that resolves to `true` if the route was successfully opened, `false` otherwise.
   */
  public openBookmarkRoute(
    bookmarkId: string,
    newTab = false
  ): Promise<boolean> {
    if (this.context === 'dataBackend') {
      return this.openRouteInNewTab(`/atlas-intern/ansicht/${bookmarkId}`);
    } else {
      const route = this.getBookmarkRoute(bookmarkId);

      if (newTab) {
        return this.openRouteInNewTab(route);
      } else {
        return this.router.navigate([route]);
      }
    }
  }

  /**
   * Generates the route for a bookmark based on the current context and additional parameters.
   *
   * @param {string} bookmarkId - The unique identifier of the bookmark.
   * @param {HyperionContext} forceContext - The unique identifier of the bookmark.
   * @param {string} slugOverwrite - slug to overwrite slug from context
   * @return {string} The generated route as an array of path segments or other relevant data, depending on the context.
   */
  getBookmarkRoute(
    bookmarkId: string,
    forceContext?: HyperionContext,
    slugOverwrite?: string
  ): string {
    const context = forceContext || this.context;
    const slug = this.getParameterFromRoute('slug') || undefined;
    if (context === 'atlasExternal' && slug) {
      const contextSegment = this.getRootByContext(context, slug);
      return `${contextSegment}ansicht/${bookmarkId}`;
    } else if (context === 'guestView') {
      const contextSegment = this.getRootByContext(
        'atlasExternal',
        slugOverwrite
      );
      return `${contextSegment}gast-ansicht/${bookmarkId}`;
    } else {
      return `/atlas-intern/ansicht/${bookmarkId}`;
    }
  }

  /**
   * Constructs the route for a specific story based on the provided story ID
   * and optional context.
   *
   * @param {string} storyId - The unique identifier for the story.
   * @param {HyperionContext} [forceContext] - An optional context to override
   * the default context.
   * @return {string} The route as an array of path segments representing
   * the story's location.
   */
  getStoryRoute(storyId: string, forceContext?: HyperionContext): string {
    const slug = this.getParameterFromRoute('slug');
    const context = forceContext || this.context;
    if (context === 'atlasExternal' && slug) {
      const contextSegment = this.getRootByContext(context, slug);
      return `${contextSegment}bericht/${storyId}`;
    } else {
      return `/atlas-intern/bericht/${storyId}`;
    }
  }

  /**
   * Navigates the application to the root path of the Atlas Intern section.
   *
   * @return {Promise<boolean>} A promise that resolves to true if navigation succeeds, or false if it fails.
   */
  public navigateAtlasRoot(): Promise<boolean> {
    const slug = this.getParameterFromRoute('slug');

    if (this.context && slug) {
      const context = this.getRootByContext(this.context, slug);
      return this.router.navigate([context]);
    } else {
      return this.router.navigate(['/atlas-intern']);
    }
  }

  /**
   * Navigates back a specified number of steps in the route history.
   *
   * @param {number} [steps] - The number of steps to navigate back. Defaults to the result of `getCancelLevel` or 0.
   * @return {void} Does not return any value.
   */
  public navigateBack(steps?: number): void {
    const backSteps = steps || this.getCancelLevel() || 0;
    if (steps === 0) return;
    this.route.url.subscribe(() => {
      const paths = this.getNestedPathFromRoot(this.route.root);

      // Remove the specified number of steps
      for (let i = 0; i < backSteps; i++) {
        if (paths.length > 1) {
          paths.pop();
        }
      }

      // Construct the new route string with properly nested brackets
      const newRoute = this.constructNestedRoute(paths);
      this.router.navigateByUrl(newRoute);
    });
  }

  /**
   * Retrieves the nested route paths and outlets starting from the root route down to the deepest child route.
   *
   * @param {ActivatedRoute} activatedRoute - The root ActivatedRoute object from which to traverse the route tree.
   * @return {{ path: string; outlet: string }[]} An array of objects representing the nested routes,
   *                                              where each object contains the path segment and the associated*/
  private getNestedPathFromRoot(
    activatedRoute: ActivatedRoute
  ): { path: string; outlet: string }[] {
    const routes: { path: string; outlet: string }[] = [];
    let currentRoute = activatedRoute;

    while (currentRoute.children.length > 0) {
      currentRoute = currentRoute.children[0];
      const path = currentRoute.snapshot.url
        .map(segment => segment.path)
        .join('/');
      if (path) {
        routes.push({ path, outlet: currentRoute.outlet || 'primary' });
      }
    }

    return routes;
  }

  /**
   * Constructs a nested route string based on the given array of route objects.
   * This method builds named outlet structures and primary path segments in a way
   * that ensures proper nesting and order.
   *
   * @param {Array<{ path: string, outlet: string }>} routes - An array of route objects where each object contains
   * the path and outlet name. The `outlet` specifies whether the path is for the primary route or a named outlet.
   * @return {string} A formatted string representing the nested route with primary path and nested outlets.
   */
  private constructNestedRoute(
    routes: { path: string; outlet: string }[]
  ): string {
    const primarySegments: string[] = [];
    let nestedOutlets = '';

    // Traverse routes from top to bottom, ensuring proper nesting
    routes.reverse().forEach(route => {
      if (route.outlet === 'primary') {
        primarySegments.push(route.path);
      } else {
        // Build the outlet structure from the outside in, ensuring correct nesting
        nestedOutlets = `(${route.outlet}:${route.path}${nestedOutlets ? `/${nestedOutlets}` : ''})`;
      }
    });

    // Combine primary path with nested named outlets
    const primaryPath = primarySegments.join('/');
    return `${primaryPath}${nestedOutlets ? `/${nestedOutlets}` : ''}`;
  }

  /**
   * get the deepest (last) child of given route
   * @param route
   */
  public getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  /**
   * Navigates to the root element by calling the method to navigate to the first child element.
   *
   * @return {any} Returns the result of navigating to the first child element.
   */
  public navigateToRoot() {
    return this.navigateToFirstChild();
  }

  navigateToFirstChild() {
    const firstChild = this.route.firstChild;
    if (firstChild && firstChild.snapshot) {
      return this.router.navigate(
        [firstChild.snapshot.url.map(segment => segment.path).join('/')],
        { relativeTo: this.route }
      );
    } else {
      return new Promise(() => true) as Promise<boolean>;
    }
  }

  /**
   * Retrieves the 'id' parameter from the deepest child route in the route tree.
   *
   * The method navigates through the route structure to find the deepest child route
   * and returns the value of the 'id' parameter from its snapshot's paramMap, if it exists.
   *
   * @return {string | null} The 'id' parameter as a string, or null if the parameter does not exist.
   */
  getIdFromRoute(): string | null {
    return this.getDeepestChild(this.route)?.snapshot?.paramMap.get('id');
  }

  /**
   * Retrieves the value of a specified parameter from the route.
   *
   * @param {string} param - The name of the route parameter to retrieve.
   * @return {string | null} The value of the parameter if found, otherwise null.
   */
  getParameterFromRoute(param: string): string | null {
    const deepestChild = this.getDeepestChild(this.route);

    return (
      deepestChild?.snapshot.paramMap.get(param) ||
      this.getParameterFromRouteTree(this.route, param)
    );
  }

  /**
   * Searches the route tree for a parameter starting from the current route
   * or its deepest child and works upwards until it finds the parameter.
   *
   * @param {ActivatedRoute} route - The current activated route.
   * @param {string} param - The name of the parameter to search for.
   * @returns {string | null} - The value of the parameter if found, otherwise null.
   */
  getParameterFromRouteTree(
    route: ActivatedRoute,
    param: string
  ): string | null {
    let currentRoute: ActivatedRoute | null = this.getDeepestChild(route);

    // Traverse upwards through parent routes until parameter is found or root is reached
    while (currentRoute) {
      const paramValue = currentRoute.snapshot.paramMap.get(param);
      if (paramValue) {
        return paramValue; // Return first match
      }
      currentRoute = currentRoute.parent; // Move to the parent
    }

    return null; // Return null if parameter not found
  }

  /**
   * Retrieves the header text for the edit form overlay.
   *
   * @return {string} The header text for the edit form overlay.
   */
  getEditFormOverlayHeader(): string {
    return this.editFormOverlayHeader;
  }

  /**
   * get editFormOverlayIcon (defined in route-data)
   */
  getEditFormOverlayIcon(): string {
    return this.editFormOverlayIcon;
  }

  /**
   * get activeEntityType (defined in route-data)
   */
  getEditFormOverlayType(): string {
    return this.activeEntityType;
  }

  /**
   * Retrieves the current cancel level.
   *
   * @return {number | undefined} The cancel level if it is set, otherwise undefined.
   */
  getCancelLevel(): number | undefined {
    return this.cancelLevel || undefined;
  }

  /**
   * Retrieves a query parameter or all query parameters from the current route.
   *
   * @param {string} [param] - The name of the query parameter to retrieve. If not provided, all query parameters will be returned.
   * @return {any} The value of the specified query parameter, or an object containing all query parameters if no parameter name is provided.
   */
  getQueryParamFromRoute(param?: string): any {
    return param
      ? this.route.snapshot.queryParams[param]
      : this.route.snapshot.queryParams;
  }

  /**
   * Checks if a router subscription exists and unsubscribes from it if present.
   *
   * @return {void} This method does not return a value.
   */
  routerSubscriptionCheck(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Redirects the application to the specified route.
   *
   * @param {any} route - The route to which the application should be navigated. It can be a string or an object containing route parameters.
   * @return {void} Does not return a value.
   */
  redirectToRoute(route: any): void {
    this.router.navigate(route);
  }

  /**
   * Opens the specified route in a new browser tab.
   *
   * @param {string} route - The URL or route to be opened in a new tab.
   * @return {void} Does not return a value.
   */
  openRouteInNewTab(route: string): Promise<boolean> {
    const domain = window.location.origin;
    window.open(domain + route, '_blank');
    return Promise.resolve(true);
  }

  /**
   * Constructs and returns the OpenAtlas link for a specific commune.
   *
   * @param {string} slug - The unique readable slug of the commune.
   * @return {string} The full URL pointing to the OpenAtlas page for the specified commune.
   */
  getOpenAtlasLink(slug: string): string {
    const domain = window.location.origin;
    return domain + `/atlas/${slug}/`;
  }

  /**
   * Generates an absolute bookmark link URL
   *
   * @param {string} bookmarkId - The unique identifier of the bookmark
   * @param {boolean} published - Whether to generate a public or internal link
   * @param {string} [slug] - Optional commune slug for public links
   * @return {string} The absolute URL to the bookmark
   */
  getAbsoluteBookmarkLink(
    bookmarkId: string,
    published: boolean = true,
    slug?: string
  ): string {
    const domain = window.location.origin;
    const communeSlug = slug || this.getParameterFromRoute('slug');

    if (published && communeSlug) {
      return `${domain}/atlas/${communeSlug}/ansicht/${bookmarkId}`;
    } else {
      return `${domain}/atlas-intern/ansicht/${bookmarkId}`;
    }
  }

  /**
   * unsubscribe router-subscription
   */
  cleanup(): void {
    this.routerSubscription.unsubscribe();
  }
}
